import React, { useState } from 'react';
import { DOCUMENT_STATES, formatDateBritish, formatDayCount } from '../../lib/compliance';
import { neverDoneLabel, rejectedLabel, expiredLabel } from '../../lib/complianceActions';

// Single document row on /portal/compliance. Shows a state-coloured
// icon, the document name, a meta line that describes the state-
// specific status, and an action button (Upload / Update / View / Acknowledge).
//
// State-driven visual treatment per the brief:
//   Outstanding     red    border + icon, "Never uploaded" meta, solid red Upload
//   Awaiting Review teal   border + icon, "Uploaded {date} · awaiting Fusion review", neutral View
//   Expiring Soon   amber  border + icon, "Expires {date} · N days", amber outline Update
//   Approved        green  border + icon, "Approved {date}" + "Expires {date}", neutral View
//   Expired         red    border + icon, "Expired {date}", solid red Update
//
// The Upload/Update buttons fire props.onUpload(meta). In this PR
// onUpload is wired to a placeholder that toasts "coming soon" – the
// upload modal lands as a follow-up PR using the same callback signature.

const STATE_STYLES = {
  [DOCUMENT_STATES.OUTSTANDING]: {
    borderLeft: '#E0290A',
    iconBg: 'rgba(224, 41, 10, 0.1)',
    iconStroke: '#E0290A',
    metaColour: '#E0290A',
    button: { variant: 'solid', accent: '#E0290A', textOnAccent: '#F9FCF1' },
  },
  [DOCUMENT_STATES.AWAITING_REVIEW]: {
    borderLeft: '#2E8FA1',
    iconBg: 'rgba(46, 143, 161, 0.1)',
    iconStroke: '#2E8FA1',
    metaColour: '#2E8FA1',
    button: { variant: 'neutral' },
  },
  [DOCUMENT_STATES.NEEDS_CLARIFICATION]: {
    borderLeft: '#E89A2E',
    iconBg: 'rgba(232, 154, 46, 0.1)',
    iconStroke: '#E89A2E',
    metaColour: '#E89A2E',
    // Two buttons rendered in tandem on this state – Reply (amber
    // outline) for a text-only response and Replace (amber solid)
    // to upload a new file. Handled inline in the component below.
    button: null,
  },
  [DOCUMENT_STATES.EXPIRING_SOON]: {
    borderLeft: '#E89A2E',
    iconBg: 'rgba(232, 154, 46, 0.1)',
    iconStroke: '#E89A2E',
    metaColour: '#E89A2E',
    button: { variant: 'outline', accent: '#E89A2E' },
  },
  [DOCUMENT_STATES.APPROVED]: {
    borderLeft: '#92D400',
    iconBg: 'rgba(146, 212, 0, 0.1)',
    iconStroke: '#92D400',
    metaColour: '#009159',
    button: { variant: 'neutral' },
  },
  [DOCUMENT_STATES.EXPIRED]: {
    borderLeft: '#E0290A',
    iconBg: 'rgba(224, 41, 10, 0.1)',
    iconStroke: '#E0290A',
    metaColour: '#E0290A',
    button: { variant: 'solid', accent: '#E0290A', textOnAccent: '#F9FCF1' },
  },
};

// Inline SVG – Lucide file-text shape, hand-coded to match the rest
// of the codebase's icon convention.
const FileTextIcon = ({ stroke = '#DADCCD' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="13" y2="17" />
  </svg>
);

const formatMeta = ({ meta, record, state }) => {
  // What this document needs is not always an upload – it can be signed,
  // completed as a form, or signed on a PDF. lib/complianceActions keys the
  // wording on satisfied_by so an acknowledge document stops claiming it was
  // "never uploaded" when there was never anything to upload.
  const satisfiedBy = meta?.satisfiedBy;
  const description = meta?.description || null;

  // A re-signature demand outranks the ordinary state copy: the supplier HAS
  // signed this, so "Expires 30 Sep" would be misleading – a newer version
  // needs signing, and the reason for it is what tells them why
  // (document-versioning §5). The record carries `resign` when one is owed.
  if (record?.resign) {
    const { state: standing, dueDate, reason } = record.resign;
    if (standing === 'resign_due') {
      return {
        primary: dueDate
          ? `Updated version – sign again by ${formatDateBritish(dueDate)} (${formatDayCount(dueDate)})`
          : 'Updated version – please sign again',
        secondary: reason || description,
      };
    }
    if (standing === 'lapsed') {
      return {
        primary: dueDate
          ? `Sign again – the deadline was ${formatDateBritish(dueDate)}`
          : 'Sign again – the deadline has passed',
        secondary: reason || description,
      };
    }
    if (standing === 'invalid_now') {
      return { primary: 'Updated version – sign again to stay compliant', secondary: reason || description };
    }
  }

  switch (state) {
    case DOCUMENT_STATES.OUTSTANDING:
      // A rejected upload maps to Outstanding, but "Never uploaded" is wrong and
      // hides the rejection (compliance review-loop brief §4/§1). Say it plainly.
      if (record?.review_decision === 'rejected') {
        return { primary: rejectedLabel(satisfiedBy), secondary: description };
      }
      return { primary: neverDoneLabel(satisfiedBy), secondary: description };
    case DOCUMENT_STATES.AWAITING_REVIEW: {
      const uploaded = record?.supplier_uploaded_at
        ? formatDateBritish(record.supplier_uploaded_at.split('T')[0])
        : null;
      return {
        primary: uploaded ? `Uploaded ${uploaded} · awaiting Fusion review` : 'Awaiting Fusion review',
        secondary: record?.expiry_date ? `Expires ${formatDateBritish(record.expiry_date)}` : null,
      };
    }
    case DOCUMENT_STATES.EXPIRING_SOON: {
      const dayCount = record?.expiry_date ? formatDayCount(record.expiry_date) : null;
      const expiry = record?.expiry_date ? formatDateBritish(record.expiry_date) : null;
      const approved = record?.expiry_approved_at
        ? formatDateBritish(record.expiry_approved_at.split('T')[0])
        : null;
      return {
        primary: expiry ? `Expires ${expiry} · ${dayCount}` : 'Expires soon',
        secondary: approved ? `Approved ${approved}` : null,
      };
    }
    case DOCUMENT_STATES.APPROVED: {
      const approved = record?.expiry_approved_at
        ? formatDateBritish(record.expiry_approved_at.split('T')[0])
        : null;
      const expiry = record?.expiry_date ? formatDateBritish(record.expiry_date) : null;
      return {
        primary: approved ? `Approved ${approved}` : 'Approved',
        // §10 — say "No expiry" rather than leaving the line off. Some approved
        // rows showed "Expires {date}" and their neighbours showed nothing, and
        // a gap where a sibling has a value reads as missing data — the
        // supplier can't tell "this one never expires" from "nobody filled the
        // date in". An acknowledgement or a one-off policy genuinely has no
        // expiry, and saying so is the whole difference.
        secondary: expiry ? `Expires ${expiry}` : 'No expiry',
      };
    }
    case DOCUMENT_STATES.EXPIRED: {
      const expiry = record?.expiry_date ? formatDateBritish(record.expiry_date) : null;
      const approved = record?.expiry_approved_at
        ? formatDateBritish(record.expiry_approved_at.split('T')[0])
        : null;
      // expiredLabel returns the whole phrase, lower-case ("expired 4 Aug 2026
      // – needs signing again"), so it reads correctly mid-sentence elsewhere.
      // It leads the row here, so capitalise it – and it says what the document
      // NEEDS, rather than leaving an expired acknowledgement with no next step.
      const phrase = expiredLabel(satisfiedBy, expiry);
      return {
        primary: phrase.charAt(0).toUpperCase() + phrase.slice(1),
        secondary: approved ? `Was approved ${approved}` : null,
      };
    }
    case DOCUMENT_STATES.NEEDS_CLARIFICATION: {
      // The admin's notes carry the human context – "please confirm
      // the £5m limit appears on page 2 of the certificate" – so we
      // surface them in full. The DocumentGroup renders an extra
      // notes panel beneath this primary/secondary line; here we
      // just announce the state.
      return {
        primary: 'Fusion requested clarification',
        secondary: description,
      };
    }
    default:
      return { primary: '', secondary: description };
  }
};

// Button label per (state, satisfiedBy). Acknowledge-type docs use a
// different verb set than upload-type docs:
//   OUTSTANDING       upload: Upload         acknowledge: Acknowledge
//   AWAITING_REVIEW   upload: View           acknowledge: View signed
//   APPROVED          upload: View           acknowledge: View signed
//   EXPIRING_SOON     upload: Update         acknowledge: Re-acknowledge
//   EXPIRED           upload: Update         acknowledge: Re-acknowledge
//
// Re-acknowledge paths land alongside Phase 6 (version-driven re-ack);
// today a recurring-ack with an expiring expiry will route through the
// same "Acknowledge" handler.
const buttonLabel = (state, satisfiedBy) => {
  const ack = satisfiedBy === 'acknowledge';
  const form = satisfiedBy === 'form';
  const pdf = satisfiedBy === 'pdf_sign';
  if (state === DOCUMENT_STATES.OUTSTANDING) return ack ? 'Acknowledge' : form ? 'Complete form' : pdf ? 'Sign PDF' : 'Upload';
  if (state === DOCUMENT_STATES.AWAITING_REVIEW) return ack ? 'View signed' : form ? 'View' : 'View';
  if (state === DOCUMENT_STATES.EXPIRING_SOON) return ack ? 'Re-acknowledge' : form ? 'Complete again' : pdf ? 'Sign again' : 'Update';
  if (state === DOCUMENT_STATES.APPROVED) return ack ? 'View signed' : 'View';
  if (state === DOCUMENT_STATES.EXPIRED) return ack ? 'Re-acknowledge' : form ? 'Complete again' : 'Update';
  return 'View';
};

const Button = ({ variant = 'neutral', accent, textOnAccent, label, onClick }) => {
  if (variant === 'solid') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="font-['Metropolis'] text-[11px] font-bold px-4 py-2 rounded-full transition-[filter] duration-150 hover:brightness-110"
        style={{ backgroundColor: accent, color: textOnAccent || '#F9FCF1', border: `1px solid ${accent}` }}
      >
        {label}
      </button>
    );
  }
  if (variant === 'outline') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="font-['Metropolis'] text-[11px] font-bold px-4 py-2 rounded-full bg-transparent transition-colors duration-150"
        style={{ color: accent, border: `1px solid ${accent}` }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(232, 154, 46, 0.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {label}
      </button>
    );
  }
  // Neutral – outlined grey
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-['Metropolis'] text-[11px] font-bold px-4 py-2 rounded-full text-fusion-mid-grey border border-line bg-transparent transition-colors duration-150 hover:border-[#DADCCD] hover:text-ink"
    >
      {label}
    </button>
  );
};

export default function DocumentRow({ entry, onUpload, onView, onReply, onRespond, onAcknowledge, onCompleteForm, onSignPdf, onDelegate, delegation, onOpenHistory, messages }) {
  const { meta, record, state } = entry;
  const isAcknowledge = meta?.satisfiedBy === 'acknowledge';
  const isForm = meta?.satisfiedBy === 'form';
  const isPdfSign = meta?.satisfiedBy === 'pdf_sign';
  const style = STATE_STYLES[state] || STATE_STYLES[DOCUMENT_STATES.OUTSTANDING];
  const { primary, secondary } = formatMeta(entry);
  const label = buttonLabel(state, meta?.satisfiedBy);
  // Acknowledge-type docs route every action (sign or read-back) to
  // the same onAcknowledge handler. The modal flips into readOnly
  // mode internally when an ack already exists for the current
  // version, so one handler covers the full lifecycle.
  const action = isAcknowledge
    ? () => onAcknowledge?.(meta, record)
    : (state === DOCUMENT_STATES.OUTSTANDING || state === DOCUMENT_STATES.EXPIRING_SOON || state === DOCUMENT_STATES.EXPIRED)
      ? () => onUpload?.(meta, state)
      : () => onView?.(meta, record);

  const isClarification = state === DOCUMENT_STATES.NEEDS_CLARIFICATION;
  // A rejected doc lives in the Outstanding group; treat it like clarification
  // for layout + the "From Fusion 2K" reason box (§4). The reviewer's message
  // (review_notes) is the same text that was emailed (§10).
  const isRejected = state === DOCUMENT_STATES.OUTSTANDING && record?.review_decision === 'rejected';
  const reasonText = (isRejected || isClarification) ? (record?.review_notes || record?.review_reason || null) : null;
  const stacked = isClarification || isRejected;
  // A conversation can also exist on a doc that's now awaiting/approved (e.g.
  // an approval comment). On those RESOLVED docs the thread is history, not a
  // live ask, so it collapses behind a toggle rather than shouting under every
  // row. On clarification/rejected docs the message IS the current ask, so it
  // stays open.
  const hasThread = Array.isArray(messages) && messages.length > 0;
  const collapsibleThread = hasThread && !stacked;
  const [threadOpen, setThreadOpen] = useState(false);
  const threadVisible = stacked || (hasThread && threadOpen);
  const columnLayout = threadVisible;

  // §7 — a LIST ROW, not a card. Each document was a white card with a shadow,
  // a radius, a coloured left border and its own margin, and seven of those is
  // a lot of scroll for rows that are uniform rather than distinct objects.
  //
  // The coloured left border went with them: the group heading directly above
  // already states the state, so the border was a second signal saying the same
  // thing — and with Outstanding and Expired now both red it had stopped
  // distinguishing them anyway.
  //
  // Rows that carry a conversation keep the card treatment: those genuinely are
  // distinct objects, with a message from Fusion inside them, and flattening
  // them would bury the one thing the supplier has to read.
  return (
    <div
      className={stacked
        ? `bg-white border border-[#E6EADB] rounded-xl shadow-lift px-4 py-3 flex flex-col items-stretch gap-3.5 mb-3`
        : `bg-white border-b border-[#EEF1E6] last:border-b-0 px-4 py-2.5 flex items-center gap-3.5 transition-colors duration-150 hover:bg-[#F7F9F0]/60 ${columnLayout ? 'flex-col items-stretch' : ''}`}
    >
      <div className="flex items-center gap-3.5 w-full">
        {/* The document icon stays; its tinted tile does not (§7). The tile was
            a third carrier of the state colour on a row that already has the
            group heading above it and the status text beside it, and at seven
            rows it turned a list into a column of chips. */}
        <span className="flex-shrink-0 flex items-center" aria-hidden="true">
          <FileTextIcon stroke={style.iconStroke} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-['Metropolis'] text-[13px] text-ink font-bold leading-tight mb-0.5">
            {meta.label}
          </div>
          <div className="font-['Metropolis'] text-[11px] text-fusion-mid-grey leading-snug">
            <span style={{ color: style.metaColour, fontWeight: 700 }}>{primary}</span>
            {secondary && <span className="text-[#51534A]"> · {secondary}</span>}
          </div>
          {record?.supplier_upload_note && (
            <div className="font-['Metropolis'] text-[11px] text-[#51534A] italic leading-snug mt-1">
              Your note: {record.supplier_upload_note}
            </div>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            {collapsibleThread && (
              <button
                type="button"
                onClick={() => setThreadOpen(o => !o)}
                aria-expanded={threadOpen}
                className="mt-1.5 inline-flex items-center gap-1.5 font-['Metropolis'] text-[11px] font-bold text-fusion-mid-grey hover:text-ink transition-colors bg-transparent border-0 p-0 cursor-pointer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                {threadOpen ? 'Hide comments' : `Show comments (${messages.length})`}
              </button>
            )}
            {/* The supplier's own history of this document – what they sent,
                what was decided, what either side said (detail-page §1). Only
                worth offering once something has actually happened to it. */}
            {onOpenHistory && state !== DOCUMENT_STATES.OUTSTANDING && (
              <button
                type="button"
                onClick={() => onOpenHistory(meta.key)}
                className="mt-1.5 inline-flex items-center gap-1.5 font-['Metropolis'] text-[11px] font-bold text-fusion-mid-grey hover:text-ink transition-colors bg-transparent border-0 p-0 cursor-pointer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                </svg>
                History
              </button>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Already with a colleague. Without this the row looked exactly as it
              did before you sent it – no way to tell you'd sent it, and every
              reason to send it again. Says WHO, because "sent to someone" is
              the bit you've forgotten a week later. */}
          {/* §10 of the tidy-up: this read "With Phil · not opened" with no
              label — the least self-explanatory thing on the page. It is the
              one row where the supplier is waiting on somebody else, and it
              never said so. Now it leads with "Waiting on", and the tooltip
              says what happens next rather than just repeating the address. */}
          {delegation && (
            <span
              title={
                // Both lines used to end "or you can complete it yourself and
                // the link stops working". That is no longer true: the action
                // button is hidden while a delegation is open, so taking it
                // back means withdrawing the request first.
                delegation.status === 'opened'
                  ? `${delegation.invitee_name || delegation.invitee_email} has opened the link you sent. Nothing more for you to do until they finish it.`
                  : `Sent to ${delegation.invitee_email || 'a colleague'}, who hasn't opened it yet. Nudge them, or send it to someone else instead.`
              }
              className="font-['Metropolis'] text-[11px] rounded-md px-2 py-1 border bg-[#E89A2E]/12 text-[#7A4E10] border-[#E89A2E]/40 whitespace-nowrap max-w-[260px] truncate">
              Waiting on {delegation.invitee_name || delegation.invitee_email}
              {delegation.status === 'opened' ? ' · opened it' : " · hasn't opened it"}
            </span>
          )}
          {/* "Send to someone else" – only on documents completed IN the app, and
              only while there's still something to do. There's nothing to
              delegate about an upload (they'd just send the file), and nothing
              to delegate once it's signed and with Fusion for review. Quiet
              and secondary: it's the exception, not the expected path. */}
          {(isForm || isAcknowledge) && onDelegate
            && state !== DOCUMENT_STATES.AWAITING_REVIEW && state !== DOCUMENT_STATES.APPROVED && (
            <button
              type="button"
              onClick={() => onDelegate(meta, record)}
              title={delegation
                ? 'Withdraw the request, send a fresh link, or hand it to someone else'
                : "Send this to a colleague who's better placed to deal with it"}
              className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] hover:text-fusion-forest underline decoration-[#51534A]/30 underline-offset-2 hover:decoration-fusion-forest transition-colors rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest whitespace-nowrap">
              {/* Once a request is OUT, this link is the only way back to it —
                  and with the action button now hidden, the only way to take
                  the document back at all. "Send to someone else to complete"
                  reads as starting a new request, so it hid the withdraw that
                  was already sitting in the modal behind it. */}
              {delegation ? 'Withdraw request' : 'Send to someone else to complete'}
            </button>
          )}
          {/* Nothing for YOU to do while it sits with someone else. The row was
              offering "Acknowledge" next to "Waiting on Alex Greybe", which
              invites the supplier to sign a document they have just asked a
              colleague to sign — and whichever of them acts, the other's work
              is wasted.

              Scoped to the states where the supplier would be ACTING. Only open
              delegations reach this component (SupplierCompliance filters on
              isOpen), so in practice an approved document never has one, but
              hiding "View" would be a worse bug than the one being fixed. */}
          {delegation && state !== DOCUMENT_STATES.AWAITING_REVIEW && state !== DOCUMENT_STATES.APPROVED ? null : isForm ? (
            // Hosted-form docs are completed + signed in the renderer, not
            // uploaded. Any state that needs the supplier's action re-opens the
            // form; awaiting/approved just views the completed copy.
            (state !== DOCUMENT_STATES.AWAITING_REVIEW && state !== DOCUMENT_STATES.APPROVED) ? (
              <Button
                variant="solid"
                accent={style.borderLeft}
                textOnAccent={(state === DOCUMENT_STATES.OUTSTANDING || state === DOCUMENT_STATES.EXPIRED) ? '#F9FCF1' : '#131313'}
                label={record ? 'Complete again' : 'Complete form'}
                onClick={() => onCompleteForm?.(meta, record)} />
            ) : (
              <Button {...style.button} label="View" onClick={() => onView?.(meta, record)} />
            )
          ) : isPdfSign ? (
            // PDF-sign docs are signed in the PDF viewer, not uploaded. Action
            // states open the signing view; awaiting/approved just view it.
            (state !== DOCUMENT_STATES.AWAITING_REVIEW && state !== DOCUMENT_STATES.APPROVED) ? (
              <Button
                variant="solid"
                accent={style.borderLeft}
                textOnAccent={(state === DOCUMENT_STATES.OUTSTANDING || state === DOCUMENT_STATES.EXPIRED) ? '#F9FCF1' : '#131313'}
                label={record ? 'Sign again' : 'Sign PDF'}
                onClick={() => onSignPdf?.(meta, record)} />
            ) : (
              <Button {...style.button} label="View" onClick={() => onView?.(meta, record)} />
            )
          ) : stacked && !isAcknowledge ? (
            // Rejected ("Not accepted") and Needs Clarification are ALIGNED:
            // both offer one action that lets the supplier reply and/or upload
            // a new copy together (onRespond → combined modal). Keep the state
            // accent (red vs amber) so the state still reads at a glance.
            <Button
              variant="solid"
              accent={style.borderLeft}
              textOnAccent={isRejected ? '#F9FCF1' : '#131313'}
              label="Reply or replace"
              onClick={() => onRespond?.(meta, record)} />
          ) : isClarification ? (
            // Acknowledge-type clarification: no file to replace, reply only.
            <Button variant="outline" accent="#E89A2E" label="Reply" onClick={() => onReply?.(meta, record)} />
          ) : (
            <Button {...style.button} label={label} onClick={action} />
          )}
        </div>
      </div>
      {columnLayout && (
        <ClarificationThread
          messages={messages}
          fallback={reasonText}
          isRejected={isRejected} />
      )}
    </div>
  );
}

// The reject/clarify conversation (§ compliance-clarification-thread). Renders
// every message in order – Fusion 2K's asks and the supplier's replies – so
// multiple rounds are all visible, not just the latest. Falls back to the
// single legacy admin message when no thread rows exist yet.
function ClarificationThread({ messages, fallback, isRejected }) {
  const tint = isRejected
    ? { bg: 'rgba(224, 41, 10, 0.06)', border: 'rgba(224, 41, 10, 0.22)', label: '#9c2b12' }
    : { bg: 'rgba(232, 154, 46, 0.06)', border: 'rgba(232, 154, 46, 0.25)', label: '#7A4E10' };

  if (!messages || messages.length === 0) {
    if (!fallback) return null;
    return (
      <div className="w-full mt-2 rounded-md px-3 py-2 font-['Metropolis'] text-[11px] text-fusion-mid-grey leading-snug whitespace-pre-wrap" style={{ backgroundColor: tint.bg, border: `1px solid ${tint.border}` }}>
        <span className="font-bold uppercase tracking-[0.15em] text-[10px] mr-2" style={{ color: tint.label }}>From Fusion 2K</span>
        {fallback}
      </div>
    );
  }

  return (
    <div className="w-full mt-2 rounded-md px-3 py-2.5 flex flex-col gap-2.5" style={{ backgroundColor: tint.bg, border: `1px solid ${tint.border}` }}>
      {messages.map((m) => {
        const isAdmin = m.author === 'admin';
        const when = m.created_at ? formatDateBritish(m.created_at.split('T')[0]) : null;
        return (
          <div key={m.id} className={`flex flex-col ${isAdmin ? 'items-start' : 'items-end'}`}>
            <div className="font-bold uppercase tracking-[0.15em] text-[9px] mb-0.5" style={{ color: isAdmin ? tint.label : '#2E8FA1' }}>
              {isAdmin ? 'From Fusion 2K' : 'Your reply'}{when ? ` · ${when}` : ''}
            </div>
            {m.body && (
              <div className="font-['Metropolis'] text-[11px] text-ink leading-snug whitespace-pre-wrap max-w-[85%]">{m.body}</div>
            )}
            {m.attachment_url && (
              <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" className="font-['Metropolis'] text-[11px] font-semibold underline underline-offset-2 mt-0.5" style={{ color: '#2E8FA1' }}>
                {m.attachment_name || 'View attachment'}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}
